// 261005 E3-P1b — thẻ khối chặng ② Khung xương: danh sách lệnh · "Lệnh không áp được" · ảnh hưởng · cảnh báo chia tập · Áp / Khôi phục bản trước / Áp lại.
// Mọi quyết định hiển thị ở `skeletonBlockView.ts` (hàm thuần, có jest); file này chỉ vẽ.
import React from 'react';
import { Alert, Popconfirm, Tag, Tooltip } from 'antd';
import Button from '../WbButton';
import type { AssistantBlock } from '../../../../types/adaptationAssistant';
import { skeletonCardView } from './skeletonBlockView';
import { disabledTip } from './assistantView';
import { BlockBody, BlockFoot, BlockShell } from './BlockShell';
import AutoApplyLine from './AutoApplyLine';
import type { TimelineHandlers } from './AssistantTimeline'; // chỉ kiểu — không vòng phụ thuộc lúc chạy


interface Props extends TimelineHandlers {
  block: AssistantBlock;
  headModifiedAt?: string | null;
  busy: boolean;
  acting: string | null;
  busyReason?: string | null;
  nameOf?: (id: string) => string;
}

const SkeletonBlockCard: React.FC<Props> = ({ block, headModifiedAt, busy, acting, busyReason, nameOf, onPreview, onApply, onUndo, onReapply, onDismiss }) => {
  const v = skeletonCardView(block, busy || !!acting, headModifiedAt, busyReason, nameOf);
  const why = busy && busyReason ? busyReason : undefined;
  return (
    <BlockShell>
      <BlockBody title={block.title} tone={v.tone} stateLabel={v.headline} running={v.phase === 'running'}>
      {v.opLabels.length > 0 && <ul style={{ margin: '4px 0', paddingLeft: 18, fontSize: 13 }}>{v.opLabels.map((l, i) => <li key={i} style={{ wordBreak: 'break-word' }}>{l}</li>)}</ul>}
      {v.detailLines.length > 0 && <ul data-testid="skeleton-detail-lines" style={{ margin: '4px 0', paddingLeft: 18, fontSize: 13, color: 'var(--wb-text-2)' }}>{v.detailLines.map((l, i) => <li key={i} style={{ wordBreak: 'break-word' }}>{l}</li>)}</ul>}
      {v.addedNote && <div style={{ margin: '4px 0', fontSize: 13 }}><Tag color="gold">do chuyển thể thêm</Tag><span>{v.addedNote}</span></div>}
      {v.phase === 'ready' && v.keepsApproval && <div data-testid="skeleton-keeps-approval" style={{ margin: '4px 0', fontSize: 13 }}><Tag color="green">giữ đã duyệt</Tag><span>{v.applyHint}</span></div>}
      {v.detail && <div style={{ fontSize: 13, color: 'var(--wb-text-2)', margin: '4px 0', wordBreak: 'break-word' }}>{v.detail}</div>}
      <AutoApplyLine note={v.autoNote} />
      {v.staleWarning && <Alert style={{ margin: '6px 0' }} type="warning" showIcon message={v.staleWarning} />}
      {v.impactLines.length > 0 && (
        <Alert style={{ margin: '6px 0' }} type="warning" showIcon message="Ảnh hưởng tới tập đã viết (kịch bản không bị sửa)" description={v.impactLines.map((l, i) => <div key={i} style={{ fontSize: 13 }}>{l}</div>)} />
      )}
      {v.anchorImpactLines.length > 0 && (
        <Alert style={{ margin: '6px 0' }} type={v.anchorImpactTone === 'ok' ? 'success' : 'warning'} showIcon message={v.anchorImpactTone === 'ok' ? 'Tập đã viết vẫn chứng minh được khoảnh khắc' : 'Tập đã viết bị ảnh hưởng (kịch bản không tự đổi theo khung xương)'} description={v.anchorImpactLines.map((l, i) => <div key={i} style={{ fontSize: 13 }}>{l}</div>)} />
      )}
      {v.rejected.length > 0 && (
        <Alert style={{ margin: '6px 0' }} type="warning" showIcon message={`${v.rejected.length} lệnh bị loại — sẽ KHÔNG được áp`} description={v.rejected.map((r, i) => <div key={i} style={{ fontSize: 13 }}>{r.label}: {r.reason}</div>)} />
      )}
      </BlockBody>
      <BlockFoot>
        {v.canPreview && <Button size="small" type="text" loading={acting === `preview-${block.id}`} onClick={() => onPreview(block)}>{v.previewLabel}</Button>}
        {!v.canPreview && why && (v.phase === 'proposed' || v.phase === 'restored' || v.phase === 'failed' || v.phase === 'stale') && (
          <Tooltip title={why}><Button size="small" type="text" disabled>{v.previewLabel}</Button></Tooltip>
        )}
        {v.phase === 'ready' && (
          <Popconfirm
            title="Áp đề xuất này?" description={v.applyHint}
            okText="Áp" cancelText="Thôi" onConfirm={() => onApply(block)} disabled={!v.canApply}
          >
            <Tooltip title={disabledTip(v.canApply, why)}><Button size="small" type="primary" disabled={!v.canApply} loading={acting === `apply-${block.id}`}>Áp</Button></Tooltip>
          </Popconfirm>
        )}
        {v.phase === 'applied' && (
          <Tooltip title={disabledTip(v.canUndo, v.undoReason ?? v.undoHint)}>
            <span>
              <Popconfirm title="Khôi phục bản trước?" description={v.undoHint} okText="Khôi phục" cancelText="Thôi" onConfirm={() => onUndo(block)} disabled={!v.canUndo}>
                <Button size="small" type="text" disabled={!v.canUndo} loading={acting === `undo-${block.id}`}>Khôi phục bản trước</Button>
              </Popconfirm>
            </span>
          </Tooltip>
        )}
        {v.phase === 'restored' && (
          <Popconfirm title="Áp lại đề xuất này?" description="Không tốn AI; chỉ áp được khi khung xương chưa khác lúc đề xuất." okText="Áp lại" cancelText="Thôi" onConfirm={() => onReapply(block)} disabled={!v.canReapply}>
            <Tooltip title={disabledTip(v.canReapply, why)}><Button size="small" type="primary" disabled={!v.canReapply} loading={acting === `reapply-${block.id}`}>Áp lại</Button></Tooltip>
          </Popconfirm>
        )}
        {v.canDismiss && <Button size="small" type="text" loading={acting === `dismiss-${block.id}`} onClick={() => onDismiss(block)}>Bỏ</Button>}
      </BlockFoot>
    </BlockShell>
  );
};

export default SkeletonBlockCard;
